import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import SublineApi from "../../services/SublineApi";

// Design System
import Input from "../../components/Input/Input";
import FormCard from "../../components/FormCard/FormCard";

const SublineDetails: React.FC = () => {
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { displayToast } = useToast();

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [lineLabel, setLineLabel] = useState<string>("");
  const [areaLabel, setAreaLabel] = useState<string>("");
  const [unitLabel, setUnitLabel] = useState<string>("");
  const [plantLabel, setPlantLabel] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      navigate("/sublines");
      return;
    }

    const fetchSubline = async (subId: string): Promise<void> => {
      try {
        const resp = await SublineApi.GetSublineById(subId);
        if (resp.success && resp.data) {
          const sub = resp.data;
          setName(sub.name || "");
          setDescription(sub.description || "");
          
          const line = sub.line;
          const area = line?.area;
          const unit = area?.productionUnit;
          const plant = unit?.plant;

          setLineLabel(line?.name || "—");
          setAreaLabel(area?.name || "—");
          setUnitLabel(unit?.name || "—");
          setPlantLabel(plant?.name || "—");
        }
      } catch (err: any) {
        displayToast("error", translateForm("error"), err.message || "", false, 3000);
        navigate("/sublines");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubline(id);
  }, [id, navigate, displayToast, translateForm]);

  const handleBack = (): void => {
    navigate("/sublines");
  };

  if (isLoading) {
    return <div className="p-4">{translateGeneral("loading")}...</div>;
  }

  return (
    <FormCard
      title={translateForm("sublineDetails")}
      icon="info"
      onSubmit={() => {}}
      onCancel={handleBack}
      cancelButtonLabel={translateForm("back") || "Voltar"}
      numColumns={12}
      disabled
    >
      {/* Hierarchy Row 1 */}
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("plant") || "Planta"}
          value={plantLabel}
          disabled
          onChange={() => {}}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("productionUnit") || "Unidade de Produção"}
          value={unitLabel}
          disabled
          onChange={() => {}}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("area") || "Área"}
          value={areaLabel}
          disabled
          onChange={() => {}}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("line") || "Linha"}
          value={lineLabel}
          disabled
          onChange={() => {}}
        />
      </div>

      {/* Subline Data */}
      <div style={{ gridColumn: "span 12" }}>
        <Input
          type="text"
          label={translateForm("name") || "Nome da Sublinha"}
          value={name}
          disabled
          onChange={() => {}}
        />
      </div>
      <div style={{ gridColumn: "span 12" }}>
        <Input
          type="textArea"
          label={translateForm("description") || "Descrição"}
          value={description}
          rows={3}
          disabled
          onChange={() => {}}
        />
      </div>
    </FormCard>
  );
};

export default SublineDetails;
